import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { GridColDef } from "@mui/x-data-grid";
import { FilterableDataGrid, FilterConfig } from "../common/FilterableDataGrid";
import { getStatusColor, formatDateTime } from "./utils";
import {
  VideoPlatformResource,
  MeetingAttendee,
  CapabilityControl,
  BatchCapabilityOperation,
  CapabilityAxis,
  CapabilityValue,
} from "../../api/video_platform";
import AttendeeCapabilityControls from "./AttendeeCapabilityControls";
import BatchCapabilityControls from "./BatchCapabilityControls";
import { useVideoPlatformApi } from "./hooks/useVideoPlatformApi";
import { useGlobalLoading } from "../../hooks/useGlobalLoading";
import { useSnackbar } from "../../hooks/useSnackbar";

interface AttendeeWithStatus {
  id: string;
  externalMeetingId: string;
  attendeeId: string;
  userId: string;
  externalUserId: string;
  attendeeName: string;
  joinedAt: string;
  leftAt: string;
  isActive: boolean;
  status: string;
  capabilities: {
    audio: CapabilityControl;
    video: CapabilityControl;
    content: CapabilityControl;
    chat: CapabilityControl;
  };
  isBanned: boolean;
}

interface MeetingAttendeesDialogProps {
  open: boolean;
  meetingId: string | null;
  meetingTitle: string;
  onClose: () => void;
}

export const MeetingAttendeesDialog: React.FC<MeetingAttendeesDialogProps> = ({
  open,
  meetingId,
  meetingTitle,
  onClose,
}) => {
  const [attendees, setAttendees] = React.useState<AttendeeWithStatus[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [totalAttendees, setTotalAttendees] = React.useState<number>(0);
  const { showSnackbar, SnackbarNode } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const api = useVideoPlatformApi();
  const { withLoading } = useGlobalLoading();

  const loadAttendees = React.useCallback(async () => {
    if (!meetingId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await VideoPlatformResource.getMeetingAttendees({
        meetingId,
        limit: 100,
      });

      const defaultCapability: CapabilityControl = { value: "SendReceive", isLocked: false };
      const attendeesWithStatus: AttendeeWithStatus[] = response.data.map(
        (attendee: any) => ({
          id: attendee.id || attendee.attendeeId || attendee.userId,
          externalMeetingId: attendee.externalMeetingId || "",
          attendeeId: attendee.attendeeId || "",
          userId: attendee.userId || "",
          externalUserId: attendee.externalUserId || "",
          attendeeName: attendee.attendeeName || "",
          joinedAt: attendee.joinedAt || "",
          leftAt: attendee.leftAt || "",
          isActive: attendee.isActive || false,
          status: attendee.isActive ? "Active" : "Inactive",
          capabilities: attendee.capabilities || {
            audio: defaultCapability,
            video: defaultCapability,
            content: defaultCapability,
            chat: defaultCapability,
          },
          isBanned: !!attendee.isBanned,
        })
      );

      setAttendees(attendeesWithStatus);
      setTotalAttendees(response.paging.total || 0);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch attendees";
      setError(errorMessage);
      setAttendees([]);
      setTotalAttendees(0);
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  React.useEffect(() => {
    if (open && meetingId) {
      loadAttendees();
    }
  }, [open, meetingId, loadAttendees]);

  const handleCopyAttendee = (attendeeId: string) => {
    const attendee = attendees.find((a) => a.id === attendeeId);
    if (attendee) {
      navigator.clipboard.writeText(JSON.stringify(attendee, null, 2));
      showSnackbar("Attendee JSON copied to clipboard!", "success");
    }
  };

  const handleUpdateCapability = async (
    attendeeId: string,
    axis: CapabilityAxis,
    value: CapabilityValue,
    lock: boolean
  ) => {
    if (!meetingId) return;

    try {
      const response = await withLoading(
        api.updateAttendeeCapabilities(meetingId, attendeeId, {
          [axis]: { value, lock },
        })
      );

      showSnackbar(`Successfully updated ${axis} capability`, "success");

      // Reflect server state locally so the toggle reads right immediately.
      setAttendees((prev) =>
        prev.map((att): AttendeeWithStatus =>
          att.id === attendeeId
            ? {
                ...att,
                capabilities: {
                  audio:
                    axis === CapabilityAxis.Audio
                      ? response.audioCapability
                      : att.capabilities.audio,
                  video:
                    axis === CapabilityAxis.Video
                      ? response.videoCapability
                      : att.capabilities.video,
                  content:
                    axis === CapabilityAxis.Content
                      ? response.contentCapability
                      : att.capabilities.content,
                  chat:
                    axis === CapabilityAxis.Chat
                      ? response.chatCapability
                      : att.capabilities.chat,
                },
              }
            : att
        )
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to update capability";
      showSnackbar(errorMessage, "error");
    }
  };

  /** Bans or unbans an attendee — toggle is keyed off `currentlyBanned`. */
  const handleToggleBan = async (
    attendeeId: string,
    currentlyBanned: boolean
  ) => {
    if (!meetingId) return;

    try {
      if (currentlyBanned) {
        await withLoading(api.unbanAttendee(meetingId, attendeeId));
        showSnackbar("User unbanned from the meeting", "success");
      } else {
        await withLoading(api.banAttendee(meetingId, attendeeId));
        showSnackbar("User banned from the meeting", "success");
      }
      setAttendees((prev) =>
        prev.map((att): AttendeeWithStatus =>
          att.id === attendeeId ? { ...att, isBanned: !currentlyBanned } : att
        )
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : currentlyBanned
            ? "Failed to unban user"
            : "Failed to ban user";
      showSnackbar(errorMessage, "error");
    }
  };

  const handleBatchUpdate = async (
    operation: BatchCapabilityOperation,
    excludeHosts: boolean,
    lock: boolean
  ) => {
    if (!meetingId) return;

    try {
      const response = await withLoading(
        api.batchUpdateAttendeeCapabilities(meetingId, operation, excludeHosts, lock)
      );

      showSnackbar(`Successfully updated ${response.updatedCount} attendee(s)`, "success");

      // Reload attendees to get the actual state from the server
      // This ensures we have accurate data including which attendees were actually updated
      await loadAttendees();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to perform batch update";
      showSnackbar(errorMessage, "error");
    }
  };

  const columns: GridColDef[] = [
    {
      field: "attendeeName",
      headerName: "Name",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.value || "-",
    },
    ...(isMobile
      ? []
      : [
          {
            field: "externalUserId",
            headerName: "External User ID",
            flex: 1,
            minWidth: 180,
            renderCell: (params) => params.value || "-",
          } as GridColDef,
        ]),
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: (params) => {
        const status = params.value || "Unknown";
        const colors = getStatusColor(status);
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Box
              component="span"
              sx={{
                backgroundColor: colors.bgcolor,
                color: colors.color,
                padding: "4px 12px",
                borderRadius: "16px",
                fontSize: "0.8125rem",
                fontWeight: 500,
                lineHeight: 1.5,
                whiteSpace: "nowrap",
              }}
            >
              {status}
            </Box>
          </Box>
        );
      },
    },
    ...(isMobile
      ? []
      : [
          {
            field: "joinedAt",
            headerName: "Joined At",
            width: 180,
            renderCell: (params) => formatDateTime(params.value),
          } as GridColDef,
          {
            field: "leftAt",
            headerName: "Left At",
            width: 180,
            renderCell: (params) => formatDateTime(params.value),
          } as GridColDef,
        ]),
    {
      field: "actions",
      headerName: "Actions",
      width: isMobile ? 72 : 280,
      sortable: false,
      renderCell: (params) => {
        const attendee = params.row as unknown as MeetingAttendee;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AttendeeCapabilityControls
              attendee={attendee}
              meetingId={meetingId || ""}
              onUpdateCapability={handleUpdateCapability}
              onToggleBan={handleToggleBan}
              disabled={!meetingId}
              extraActions={[
                {
                  key: "copy",
                  label: "Copy JSON",
                  icon: <ContentCopyIcon fontSize="small" />,
                  onClick: () => handleCopyAttendee(params.row.id),
                },
              ]}
            />
            {!isMobile && (
              <Tooltip title="Copy JSON">
                <IconButton
                  size="small"
                  onClick={() => handleCopyAttendee(params.row.id)}
                  color="default"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  const filters: FilterConfig[] = [
    {
      label: "Status",
      field: "status",
      options: [
        { value: "ALL", label: "All Statuses" },
        { value: "ACTIVE", label: "Active" },
        { value: "INACTIVE", label: "Inactive" },
      ],
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <div>Meeting Attendees - {meetingTitle}</div>
          {totalAttendees > 0 && (
            <Box
              component="span"
              sx={{
                fontSize: "0.875rem",
                color: "text.secondary",
                fontWeight: "normal",
              }}
            >
              Total unique attendees: {totalAttendees}
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BatchCapabilityControls
            meetingId={meetingId || ""}
            onBatchUpdate={handleBatchUpdate}
            disabled={!meetingId || loading}
            isAudioLocked={attendees.length > 0 && attendees.every(a => a.capabilities?.audio?.isLocked)}
            isVideoLocked={attendees.length > 0 && attendees.every(a => a.capabilities?.video?.isLocked)}
            isContentLocked={attendees.length > 0 && attendees.every(a => a.capabilities?.content?.isLocked)}
            isChatLocked={attendees.length > 0 && attendees.every(a => a.capabilities?.chat?.isLocked)}
          />
          <Tooltip title="Refresh attendees">
            <IconButton
              onClick={loadAttendees}
              disabled={loading}
              size="small"
              color="primary"
            >
              {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {loading && <Box>Loading attendees...</Box>}
          {error && <Box sx={{ color: "error.main" }}>Error: {error}</Box>}
          {!loading && !error && (
            <FilterableDataGrid
              rows={attendees}
              columns={columns}
              filters={filters}
              searchFields={[
                "attendeeName",
                "userId",
                "externalUserId",
                "attendeeId",
              ]}
              searchPlaceholder="Search by name, user ID, or attendee ID"
              height={500}
              pageSize={10}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {SnackbarNode}
    </Dialog>
  );
};
